import { environment } from '../environments/environment';
import { Observable } from 'rxjs';
import { Component, OnInit, ViewEncapsulation, AfterViewInit, OnDestroy, NgZone, ViewContainerRef, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { FormControl } from '@angular/forms';
import { AuthService, IUserInfo, MessageBrokerService, ErrorModel } from './shared/core/index';
import { ProjectItem } from './project/index';
import { AppService } from './app.service';
import { NotificationsService, Notification } from './shared/helper-components/index';
import { ActivatedRoute, Router } from '@angular/router';
import { GlobalErrorHandler } from './shared/core/error-handler';
import { HttpErrorModel } from './shared/core/error.model';
import { debounceTime, distinctUntilChanged, switchMap, map } from 'rxjs/operators';
import { FileUploadService } from './shared/core/file-upload.service';
import { HttpEventType } from '@angular/common/http';
import { IHelpDocsReference } from './project/project.model';
import { MatMenuTrigger } from '@angular/material/menu';

@Component({
  selector: 'app-root',
  standalone: false,
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./app.component.css'],
  templateUrl: './app.component.html'
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  appUserInfo: IUserInfo = new IUserInfo();
  favoriteProjects: ProjectItem[] = [];
  recentProjects: ProjectItem[] = [];
  isProjectPrefLoading: boolean;
  term = new FormControl();
  searchResults$: Observable<ProjectItem[]>;
  currentProjectName: string;
  isClearanceUser: boolean;
  isProductionUser: boolean;
  errorLoadingProjects: boolean;
  viewContainerRef: ViewContainerRef;
  public openedProject: ProjectItem;
  defaultErrorMessage: string = 'Apologies, something went wrong, please mention the following error/code when requesting help: ';
  environmentName = '';
  @ViewChild('appMainSection', { static: false }) appMainSection: ElementRef;
  @ViewChild('helpRefTrigger', { static: false }) helpRefTrigger: MatMenuTrigger;
  helpReferenceDocuments: IHelpDocsReference[] = [];
  @ViewChild('documentInput', { static: false }) documentInput: ElementRef;
  downloadInProcess: boolean = false;
  downloadTooltip: string = '';
  isProjectLoaded = false;
  helpReferenceDocumentsLoaded = false;
  private helpRefCloseTimer: ReturnType<typeof setTimeout> | null = null;
  private helpRefPanel: HTMLElement | null = null;

  public isMobile = false;
  public sidenavMode: 'side' | 'over' = 'side';
  /** Open state separate from isMobile so hamburger can toggle overlay without fighting mode logic. */
  public sidenavOpened = true;

  /**
   * Collapse sidenav (mode=over, closed) at tablet/mobile.
   * Matches SharedUtils.IsMobile (< 1025), visual-baseline tablet 1024×768 (VC-005 / VR-001),
   * and custom-materialize $medium-screen: 1024px.
   */
  private static readonly SIDENAV_COLLAPSE_MAX_WIDTH = 1024;

  constructor(
    public _authService: AuthService,
    private _appService: AppService,
    private ngZone: NgZone,
    private _notificationsService: NotificationsService,
    private _messageBrokerService: MessageBrokerService,
    private _router: Router,
    private _route: ActivatedRoute,
    viewContainerRef: ViewContainerRef,
    private _errorHandler: GlobalErrorHandler,
    private cd: ChangeDetectorRef,
    private _fileUploadService: FileUploadService
  ) {
    this.viewContainerRef = viewContainerRef;
    this.environmentName = _authService.ENV ? _authService.ENV : environment.name;
    this.updateSidenavMode();
    window.addEventListener('resize', () => {
      this.ngZone.run(() => this.updateSidenavMode());
    });
  }

  public ngOnInit() {
    console.log('Start Main Component');

    if (typeof this._authService.userInfo.accessToken !== 'undefined') {
      var redirection = localStorage.getItem('redirectUrl');
      if (redirection) {
        localStorage.removeItem('redirectUrl');
        this._router.navigateByUrl(redirection);
      }
      if (this._authService.userInfo != null && this._authService.userInfo.accessToken) {
        this.appUserInfo = this._authService.userInfo;
        this.refreshLeftNav();
        this.initSearchResults();
        this.subScribeProjectNameChanges();
        this.subScribeProjecDeleted();
        this.isClearanceUser = this._authService.isUserInRole(environment.clearanceRole);
        this.isProductionUser = !this._authService.isUserInRole(environment.clearanceRole);
        this.subScribeRefreshLeftNav();
      } else {
        if (this._authService.error !== null) {
          let error: ErrorModel = new ErrorModel();
          error.reloadRoute = '/';
          error.shouldRefreshApp = true;

          if (this._authService.error) {
            var httpError: HttpErrorModel;
            let err = this._authService.error.json();
            if (err instanceof Array && err.length > 0) {
              httpError = err[0];
              error.httpError = <HttpErrorModel>httpError;
            } else {
              Object.assign(error, err);
            }
          }
          error.Message = this.defaultErrorMessage;
          this._messageBrokerService.showErrorScreen(error);
        }
      }
    }
    this.verifyHtmlCanvas();
    this.initUploader();
    this._appService.downloadingObservable.subscribe((downloading) => {
      this.activateDownloadTooltip(downloading);
    });

    this._appService.projectHeaderUpdate
      .subscribe(({ projectId, projectName }) => {
        const project = new ProjectItem(projectId, projectName);
        this._messageBrokerService.setCurrentProject(project, true);
        this.isProjectLoaded = this._appService.isProjectLoaded;
        this.subScribeProjectNameChanges();
      });

    this._appService.displayNameUpdate.subscribe((name) => {
      this.currentProjectName = name;
    });
  }

  ngAfterViewInit() {
    // Sync open/mode after view exists (constructor may run before draw).
    this.updateSidenavMode();
  }

  ngOnDestroy(): void {
    this.cancelHelpRefMenuClose();
    this.unbindHelpRefPanelHover();
  }

  updateSidenavMode() {
    const shouldCollapse = window.innerWidth <= AppComponent.SIDENAV_COLLAPSE_MAX_WIDTH;
    const enteringCollapse = shouldCollapse && !this.isMobile;
    this.isMobile = shouldCollapse;
    this.sidenavMode = shouldCollapse ? 'over' : 'side';
    if (!shouldCollapse) {
      // Desktop: persistent side drawer.
      this.sidenavOpened = true;
    } else if (enteringCollapse) {
      // Entering tablet/mobile: close overlay so main is full width (VC-005).
      this.sidenavOpened = false;
    }
    // markForCheck only — detectChanges() is unsafe from constructor/resize mid-CD.
    this.cd.markForCheck();
  }

  toggleMobileSidenav() {
    if (!this.isMobile) {
      return;
    }
    this.sidenavOpened = !this.sidenavOpened;
  }

  initSearchResults(): void {
    this.searchResults$ = this.term.valueChanges.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      switchMap(val => {
        if (typeof val === 'string' && val.length >= 4) {
          return this._appService.rawSearch(false, val).pipe(
            map(results => Array.isArray(results) ? [...results] : [])
          );
        } else {
          return new Observable<ProjectItem[]>(observer => { observer.next([]); observer.complete(); });
        }
      })
    );
  }

  displayProjectFn(project?: ProjectItem): string {
    return project && typeof project === 'object' ? project.ProjectName : '';
  }

  selectSearchedProject(project: ProjectItem): void {
    if (project && project.ProjectId) {
      this._router.navigate(['/dashboard', project.ProjectId]);
      this.term.setValue('');
    }
  }

  activateDownloadTooltip(downloading) {
    this.downloadInProcess = downloading;
    this.downloadTooltip = `Downloading. ${this._appService.downloadFileNames.length} pending file(s)`;
  }

  verifyHtmlCanvas() {
    if (!HTMLCanvasElement.prototype.toBlob) {
      Object.defineProperty(HTMLCanvasElement.prototype, 'toBlob', {
        value: function(callback, type, quality) {
          var canvas = this;
          setTimeout(function() {
            var binStr = atob(canvas.toDataURL(type, quality).split(',')[1]),
              len = binStr.length,
              arr = new Uint8Array(len);
            for (var i = 0; i < len; i++) {
              arr[i] = binStr.charCodeAt(i);
            }
            callback(new Blob([arr], { type: type || 'image/png' }));
          });
        }
      });
    }
  }

  goToItemList(sortByField?: string): void {
    if (!sortByField) sortByField = 'ModifiedDate';
    this._router.navigate(
      ['projects', this.openedProject ? this.openedProject.ProjectId : 'undefined', 'items'],
      { queryParams: { sortBy: sortByField, sortOrder: 'DESC', projectName: this.openedProject.ProjectName } }
    );
  }

  goToReports(): void {
    this._router.navigate(['reports']);
  }

  subScribeProjectNameChanges(): void {
    this._messageBrokerService.getCurrentProject().subscribe((project: ProjectItem) => {
      if (project != null) {
        this.currentProjectName = project.ProjectName;
        this.openedProject = project;
        if (project.ProjectId) {
          this.addProjectToRecentList(project);
        }
      } else {
        this.openedProject = null;
        this.currentProjectName = '';
      }
      this._appService.isProjectLoaded = true;
      this.cd.detectChanges();
      this.getHelpReferenceDocuments();
    });
  }

  subScribeProjecDeleted(): void {
    this._messageBrokerService.getDeletedProject().subscribe((projectId: string) => {
      if (projectId) {
        if (this.recentProjects && this.recentProjects.length > 0) {
          var index = this.recentProjects.findIndex(it => it.ProjectId == projectId);
          if (index >= 0) this.recentProjects.splice(index, 1);
        }
        if (this.favoriteProjects && this.favoriteProjects.length > 0) {
          var index = this.favoriteProjects.findIndex(it => it.ProjectId == projectId);
          if (index >= 0) this.favoriteProjects.splice(index, 1);
        }
      }
    });
  }

  subScribeRefreshLeftNav(): void {
    this._messageBrokerService.subscribeRefreshLeftNav().subscribe((projectId: string) => {
      this.refreshLeftNav();
    });
  }

  openProjOptions(proj): void {
    this.openedProject = proj;
  }

  isAddToFavDisabled(): boolean {
    if (this.openedProject != null && this.favoriteProjects != null &&
      this.favoriteProjects.findIndex(it => it.ProjectId == this.openedProject.ProjectId) >= 0) {
      return true;
    }
    return false;
  }

  addProjectToFavList(): void {
    if (this.openedProject != null) {
      if (this.isAddToFavDisabled()) return;
      this.isProjectPrefLoading = true;
      this.cd.detectChanges();
      this._appService.addFavorite(this.openedProject)
        .subscribe(status => {
          if (status) {
            this.addFavoriteLocal(this.openedProject);
            this.refreshLeftNav();
            this._notificationsService.add(new Notification('success',
              this.openedProject.ProjectName + ' added to favorites'));
          } else {
            this.isProjectPrefLoading = false;
            this.cd.detectChanges();
            this._notificationsService.add(new Notification('error',
              'Failed to add ' + this.openedProject.ProjectName + ' to favorites'));
          }
        },
          err => {
            this.isProjectPrefLoading = false;
            this.cd.detectChanges();
            this._errorHandler.processError(err);
          });
    }
  }

  removeFromFavList(): void {
    if (this.openedProject != null) {
      this.isProjectPrefLoading = true;
      this.cd.detectChanges();
      this._appService.deleteFavorite(this.openedProject)
        .subscribe(status => {
          if (status) {
            this.removeFavoriteLocal(this.openedProject);
            this.refreshLeftNav();
            this._notificationsService.add(new Notification('success',
              this.openedProject.ProjectName + ' removed from favorites'));
          } else {
            this.isProjectPrefLoading = false;
            this.cd.detectChanges();
            this._notificationsService.add(new Notification('error',
              'Failed to remove ' + this.openedProject.ProjectName + ' from favorites'));
          }
        },
          err => {
            this.isProjectPrefLoading = false;
            this.cd.detectChanges();
            this._errorHandler.processError(err);
          });
    }
  }

  private addFavoriteLocal(project: ProjectItem): void {
    if (!project) {
      return;
    }
    if (!this.favoriteProjects) {
      this.favoriteProjects = [];
    }
    if (this.favoriteProjects.findIndex(it => it.ProjectId == project.ProjectId) >= 0) {
      return;
    }
    const fav = Object.assign(new ProjectItem(), project);
    fav.PreferenceType = 'Favorite Project';
    this.favoriteProjects = this.favoriteProjects.concat(fav).sort((a, b) => {
      const left = a.ProjectName.toLowerCase();
      const right = b.ProjectName.toLowerCase();
      return left > right ? 1 : (left < right ? -1 : 0);
    });
    this.cd.detectChanges();
  }

  private removeFavoriteLocal(project: ProjectItem): void {
    if (!this.favoriteProjects || !project) {
      return;
    }
    this.favoriteProjects = this.favoriteProjects.filter(it => it.ProjectId != project.ProjectId);
    this.cd.detectChanges();
  }

  addProjectToRecentList(project: ProjectItem): void {
    if (!this.isProjectPrefLoading && !this.errorLoadingProjects) {
      var proj = new ProjectItem();
      proj.ProjectId = project.ProjectId;
      proj.ProjectName = project.ProjectName;
      proj.TotalCICount = project.TotalCICount;
      proj.NewCICount = project.NewCICount;
      proj.UserId = this._authService.userInfo.userId;
      proj.SortOrder = 1;
      if (!this.recentProjects) this.recentProjects = [];
      if (this.recentProjects.length == 0) {
        this.recentProjects.push(proj);
      } else {
        var existingItemIndx = this.recentProjects.findIndex(it => it.ProjectId == project.ProjectId);
        if (existingItemIndx >= 0) {
          this.recentProjects.splice(existingItemIndx, 1);
        }
        this.recentProjects.splice(0, 0, proj);
        if (this.recentProjects.length > 2) {
          this.recentProjects.splice(2, this.recentProjects.length - 2);
        }
      }
    }
  }

  refreshLeftNav(): void {
    if (this._authService.accessDetails.access_token) {
      this.isProjectPrefLoading = true;
      this.errorLoadingProjects = false;
      this._appService.getUserPreferences()
        .subscribe(projects => {
          if (projects != null) {
            let fav = projects.filter(p => p.PreferenceType == 'Favorite Project');
            this.favoriteProjects = fav.sort((a, b) => {
              return (a.ProjectName.toLowerCase() > b.ProjectName.toLowerCase() ? 1 :
                (a.ProjectName.toLowerCase() < b.ProjectName.toLowerCase() ? -1 : 0));
            });
            this.recentProjects = projects.filter(p => p.PreferenceType == 'Recent Project');
            if (this.recentProjects.length > 0) {
              this._messageBrokerService.setCurrentProject(this.recentProjects[0]);
            }
          }
          this.isProjectPrefLoading = false;
          this.cd.detectChanges();
        },
          err => {
            this.isProjectPrefLoading = false;
            this.errorLoadingProjects = err.status != 404;
            this.cd.detectChanges();
            if (err.status != 404) {
              this._errorHandler.processError(err);
            }
          });
    }
  }

  activeRoute(routename: string): boolean {
    return this._router.url.indexOf(routename) > -1;
  }

  onMainSectionScroll() {
    const mainElement = this.appMainSection.nativeElement;
    const scrollTop = mainElement.scrollTop;
    const elementHeight = mainElement.clientHeight;
    const scrollHeight = mainElement.scrollHeight;
    var isMainScrollAtBottom = Math.ceil((scrollTop + elementHeight) / 10) * 10 >= Math.floor(scrollHeight / 10) * 10;
    if (isMainScrollAtBottom) {
      this._appService.scrollBottomReachedEvent.emit();
    }
  }

  openHelpRefMenu(): void {
    this.cancelHelpRefMenuClose();
    if (this.helpRefTrigger && !this.helpRefTrigger.menuOpen) {
      this.helpRefTrigger.openMenu();
    }
  }

  scheduleCloseHelpRefMenu(): void {
    this.cancelHelpRefMenuClose();
    this.helpRefCloseTimer = setTimeout(() => {
      if (this.helpRefTrigger && this.helpRefTrigger.menuOpen) {
        this.helpRefTrigger.closeMenu();
      }
      this.helpRefCloseTimer = null;
    }, 200);
  }

  cancelHelpRefMenuClose(): void {
    if (this.helpRefCloseTimer) {
      clearTimeout(this.helpRefCloseTimer);
      this.helpRefCloseTimer = null;
    }
  }

  onHelpRefMenuOpened(): void {
    setTimeout(() => this.bindHelpRefPanelHover(), 0);
  }

  onHelpRefMenuClosed(): void {
    this.unbindHelpRefPanelHover();
    this.cancelHelpRefMenuClose();
  }

  private bindHelpRefPanelHover(): void {
    this.unbindHelpRefPanelHover();
    this.helpRefPanel = document.querySelector('.help-ref-menu-panel');
    if (!this.helpRefPanel) {
      return;
    }
    this.helpRefPanel.addEventListener('mouseenter', this.onHelpRefPanelEnter);
    this.helpRefPanel.addEventListener('mouseleave', this.onHelpRefPanelLeave);
  }

  private unbindHelpRefPanelHover(): void {
    if (!this.helpRefPanel) {
      return;
    }
    this.helpRefPanel.removeEventListener('mouseenter', this.onHelpRefPanelEnter);
    this.helpRefPanel.removeEventListener('mouseleave', this.onHelpRefPanelLeave);
    this.helpRefPanel = null;
  }

  private onHelpRefPanelEnter = (): void => {
    this.ngZone.run(() => this.cancelHelpRefMenuClose());
  };

  private onHelpRefPanelLeave = (): void => {
    this.ngZone.run(() => this.scheduleCloseHelpRefMenu());
  };

  openHelpReferenceDocument(event): void {
    var fileExtension = event.path.substring(event.path.lastIndexOf('.'));
    if (event.name.endsWith(fileExtension)) fileExtension = '';
    const filename = encodeURIComponent(event.name + fileExtension);
    this._appService.getSignedUrlInline(event.path, 'Generic', 'GET', 'ref', 'ref', filename)
      .subscribe(signedUrl => {
        event.url = signedUrl.url;
        window.open(event.url);
      });
  }

  uploadHelpReferenceDocument(event): void {
    this.documentInput.nativeElement.click();
    this.documentInput.nativeElement.dataset.docRefId = event.id;
  }

  deleteHelpReferenceDocument(event): void {
    const docRefId = event.id;
    const projectId = this.openedProject ? this.openedProject.ProjectId : 'Generic';
    this._appService.getSignedUrl(encodeURIComponent(event.path), projectId, 'DELETE', 'ref', 'delete', docRefId).subscribe(
      signedUrl => {
        this.getHelpReferenceDocuments();
        this._notificationsService.add(new Notification('success', event.name + ' was removed'));
      },
      (error: any) => {
        this.getHelpReferenceDocuments();
        this._notificationsService.add(new Notification('error', 'Failed to remove ' + event.name));
      });
  }

  newHelpReferenceDocument(): void {
    this.documentInput.nativeElement.click();
    this.documentInput.nativeElement.dataset.docRefId = 'new';
  }

  getHelpReferenceDocuments() {
    this.helpReferenceDocuments = [];
    const projectId = this.openedProject ? this.openedProject.ProjectId : 'Generic';
    this._appService.getReferenceDocuments(projectId)
      .subscribe(documents => {
        if (documents != null) {
          this.helpReferenceDocuments = documents.map(doc => {
            const helpDoc: IHelpDocsReference = {
              id: doc.HelpDocsReferenceId,
              name: doc.Name,
              path: doc.Path,
              isDefault: doc.IsDefault,
              toolTipDelay: doc.Name.length > 15 ? 500 : 5000,
              toolTipDelayM: doc.Name.length > 15 ? 500 : 5000
            };
            return helpDoc;
          });
          this.helpReferenceDocumentsLoaded = true;
          this.cd.detectChanges();
        }
      },
        err => {
          if (err.status != 404) {
            this._errorHandler.processError(err);
          }
        });
  }

  initUploader() {
    // No-op: file upload is handled via onDocumentFileSelected and FileUploadService
  }

  onDocumentFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];
    const docRefId = this.documentInput.nativeElement.dataset.docRefId;
    this.getSignedUrlToUpload(file, docRefId);

    // Reset input so same file can be re-selected
    input.value = '';
  }

  getSignedUrlToUpload(file: File, docRefId: string) {
    const projectId = this.openedProject ? this.openedProject.ProjectId : 'Generic';
    this._appService.getSignedUrl(encodeURIComponent(file.name), projectId, 'PUT', 'ref', 'ref', docRefId).subscribe(
      signedUrl => {
        const uploadUrl = signedUrl.url.toString();
        this._fileUploadService.uploadRaw(file, uploadUrl, 'PUT').subscribe({
          next: (event) => {
            if (event.type === HttpEventType.Response) {
              this.getHelpReferenceDocuments();
              this._notificationsService.add(new Notification('success', file.name + ' successfully uploaded'));
            }
          },
          error: (err) => {
            this._notificationsService.add(new Notification('error', 'Failed to upload ' + file.name));
            console.error('onErrorItem, file: ' + file.name + ', error: ' + err.message);
          }
        });
      });
  }
}
